import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import { API } from "../App";
import { useAuth } from "../App";
import { Plus, Search, Filter, Edit2, Trash2, X } from "lucide-react";

const CAPAPage = () => {
  const { t } = useTranslation('capa');
  const { isAdmin, isAuditor } = useAuth();
  const [capas, setCAPAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCAPA, setEditingCAPA] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "corrective",
    priority: "medium",
    sector: "life-sciences",
    org_id: "",
    org_name: "",
    vendor_id: "",
    due_date: ""
  });

  const fetchCAPAs = async () => {
    try {
      let url = `${API}/capa`;
      const params = [];
      if (filter !== "all") params.push(`status=${filter}`);
      if (sectorFilter !== "all") params.push(`sector=${sectorFilter}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setCAPAs(response.data);
    } catch (error) {
      console.error("Error fetching CAPAs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCAPAs();
  }, [filter, sectorFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCAPA) {
        await axios.put(`${API}/capa/${editingCAPA.capa_id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/capa`, formData, { withCredentials: true });
      }
      setShowModal(false);
      setEditingCAPA(null);
      setFormData({
        title: "",
        description: "",
        type: "corrective",
        priority: "medium",
        sector: "life-sciences",
        org_id: "",
        org_name: "",
        vendor_id: "",
        due_date: ""
      });
      fetchCAPAs();
    } catch (error) {
      console.error("Error saving CAPA:", error);
    }
  };

  const handleEdit = (capa) => {
    setEditingCAPA(capa);
    setFormData({
      title: capa.title,
      description: capa.description,
      type: capa.type,
      priority: capa.priority,
      sector: capa.sector,
      status: capa.status,
      due_date: capa.due_date || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (capaId) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/capa/${capaId}`, { withCredentials: true });
      fetchCAPAs();
    } catch (error) {
      console.error("Error deleting CAPA:", error);
    }
  };

  const filteredCAPAs = capas.filter(capa => 
    capa.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    capa.capa_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      open: "badge-red",
      "in-progress": "badge-amber",
      closed: "badge-green",
      verified: "badge-cyan"
    };
    return badges[status] || "badge-gray";
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: "badge-gray",
      medium: "badge-blue",
      high: "badge-amber",
      critical: "badge-red"
    };
    return badges[priority] || "badge-gray";
  };

  return (
    <QMSLayout title={t('capa:title')}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[#8B95A5]">{t('subtitle')}</p>
        </div>
        {(isAdmin || isAuditor) && (
          <button
            onClick={() => {
              setEditingCAPA(null);
              setFormData({
                title: "",
                description: "",
                type: "corrective",
                priority: "medium",
                sector: "life-sciences",
                due_date: ""
              });
              setShowModal(true);
            }}
            className="btn-primary"
            data-testid="create-capa-btn"
          >
            <Plus size={18} /> {t('newCapa')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6" data-testid="capa-filters">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={18} />
          <input
            type="text"
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="qms-input pl-10"
            data-testid="capa-search"
          />
        </div>

        {/* Status Filter */}
        <div className="filter-pills">
          {["all", "open", "in-progress", "closed", "verified"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`filter-pill ${filter === status ? "active" : ""}`}
              data-testid={`filter-${status}`}
            >
              {status === "all" ? t('allStatus') : status.replace("-", " ")}
            </button>
          ))}
        </div>

        {/* Sector Filter */}
        <select
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="qms-select w-auto"
          data-testid="sector-filter"
        >
          <option value="all">{t('sectors.all')}</option>
          <option value="life-sciences">{t('sectors.lifeSciences')}</option>
          <option value="aerospace">{t('sectors.aerospace')}</option>
          <option value="industrial">{t('sectors.industrial')}</option>
          <option value="iot">IoT</option>
        </select>
      </div>

      {/* Table */}
      <div className="qms-card overflow-hidden" data-testid="capa-table">
        <div className="overflow-x-auto">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('table.id')}</th>
                <th>{t('description')}</th>
                <th>{t('table.type')}</th>
                <th>{t('table.status')}</th>
                <th>{t('table.priority')}</th>
                <th>{t('table.sector')}</th>
                <th>{t('table.dueDate')}</th>
                {(isAdmin || isAuditor) && <th>{t('table.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-[#8B95A5]">{t('common:loading', 'Loading...')}</td>
                </tr>
              ) : filteredCAPAs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-[#8B95A5]">{t('noCapa')}</td>
                </tr>
              ) : (
                filteredCAPAs.map((capa) => (
                  <tr key={capa.capa_id} data-testid={`capa-row-${capa.capa_id}`}>
                    <td className="font-mono text-[#38BDF8]">{capa.capa_id}</td>
                    <td className="text-white">{capa.title}</td>
                    <td>
                      <span className="badge badge-blue capitalize">{capa.type}</span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(capa.status)} capitalize`}>
                        {capa.status.replace("-", " ")}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getPriorityBadge(capa.priority)} capitalize`}>
                        {capa.priority}
                      </span>
                    </td>
                    <td className="capitalize">{capa.sector.replace("-", " ")}</td>
                    <td>{capa.due_date || "-"}</td>
                    {(isAdmin || isAuditor) && (
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(capa)}
                            className="p-1 text-[#8B95A5] hover:text-[#38BDF8] transition-colors"
                            data-testid={`edit-capa-${capa.capa_id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(capa.capa_id)}
                              className="p-1 text-[#8B95A5] hover:text-[#F87171] transition-colors"
                              data-testid={`delete-capa-${capa.capa_id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg" data-testid="capa-modal">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
              <h3 className="font-['Syne'] text-lg font-semibold text-white">
                {editingCAPA ? t('editCapa') : t('newCapa')}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-[#8B95A5] hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('form.title')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="qms-input"
                  required
                  data-testid="capa-title-input"
                />
              </div>
              
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('form.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="qms-input min-h-[100px]"
                  required
                  data-testid="capa-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('form.type')}</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="qms-select"
                    data-testid="capa-type-select"
                  >
                    <option value="corrective">{t('type.corrective')}</option>
                    <option value="preventive">{t('type.preventive')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('form.priority')}</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="qms-select"
                    data-testid="capa-priority-select"
                  >
                    <option value="low">{t('priority.low')}</option>
                    <option value="medium">{t('priority.medium')}</option>
                    <option value="high">{t('priority.high')}</option>
                    <option value="critical">{t('priority.critical')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('form.sector')}</label>
                  <select
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="qms-select"
                    data-testid="capa-sector-select"
                  >
                    <option value="life-sciences">{t('sectors.lifeSciences')}</option>
                    <option value="aerospace">{t('sectors.aerospace')}</option>
                    <option value="industrial">{t('sectors.industrial')}</option>
                    <option value="iot">IoT</option>
                  </select>
                </div>

                <OrgSelector
                  value={formData.org_id}
                  onChange={(orgId, orgName) => setFormData({...formData, org_id: orgId, org_name: orgName})}
                  showVendorId={true}
                  vendorId={formData.vendor_id}
                  onVendorIdChange={v => setFormData({...formData, vendor_id: v})}
                />

                {editingCAPA && (
                  <div>
                    <label className="block text-[#8B95A5] text-sm mb-1">{t('form.status')}</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="qms-select"
                      data-testid="capa-status-select"
                    >
                      <option value="open">{t('statusOptions.open')}</option>
                      <option value="in-progress">{t('statusOptions.inProgress')}</option>
                      <option value="closed">{t('statusOptions.closed')}</option>
                      <option value="verified">{t('statusOptions.verified')}</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('form.dueDate')}</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="qms-input"
                  data-testid="capa-duedate-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1" data-testid="capa-submit-btn">
                  {editingCAPA ? t('updateCapa') : t('createCapa')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-ghost"
                >
                  {t('common:cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default CAPAPage;
